import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader, useToast } from "components/admin";
import RichTextEditor from "components/admin/RichTextEditor";
import { Button, Checkbox, Input, TreeSelect } from "components/common";
import { normalizeUploadedImageUrl, uploadImage } from "apis/uploadImage";
import {
  createSectionContentCard,
  getAdminSectionContentCards,
  getAdminSectionContentCardById,
  updateSectionContentCard,
} from "apis/adminSectionContentCards";
import { getAllAdminNavMenus } from "apis/adminNavMenus";
import {
  useActiveLanguagesQuery,
  useAdminCreateMutation,
  useAdminDetailQuery,
  useAdminListQuery,
  useAdminUpdateMutation,
} from "hooks";
import "../../../styles/admin-shared.scss";

const createEmptyPage = () => ({ navMenuId: "", sortOrder: 0, isActive: true });

const getFileNameFromUrl = (url) => {
  if (!url) return "";
  const cleanUrl = url.split("?")[0];
  return decodeURIComponent(cleanUrl.substring(cleanUrl.lastIndexOf("/") + 1));
};

const SectionContentCardsForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const imageInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const [errors, setErrors] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    imgURL: "",
    color: "#4db748",
    textColor: "#000000",
    isBorder: true,
    isActive: true,
    translation: [],
    pages: [createEmptyPage()],
  });

  const { data: languages = [] } = useActiveLanguagesQuery();
  const { data: menuData = { items: [] } } = useAdminListQuery(
    "nav-menus-options",
    { pageSize: 1000, languageCode: "vi" },
    getAllAdminNavMenus,
  );
  const menus = menuData.items;
  const { data: detail, error: detailError } = useAdminDetailQuery(
    "section-content-cards",
    id,
    getAdminSectionContentCardById,
  );
  const createMutation = useAdminCreateMutation(
    "section-content-cards",
    createSectionContentCard,
  );
  const updateMutation = useAdminUpdateMutation(
    "section-content-cards",
    updateSectionContentCard,
  );
  const loading = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (!id && languages.length) {
      setFormData((prev) => ({
        ...prev,
        translation: languages.map((l) => ({
          languageId: l.id,
          title: "",
          subtitle: "",
          description: "",
        })),
      }));
    }
  }, [id, languages]);

  useEffect(() => {
    if (!detail) return;
    setFormData({
      imgURL: normalizeUploadedImageUrl(detail.imgURL || ""),
      color: detail.color || "#ffffff",
      textColor: detail.textColor || "#123D2A",
      isBorder: detail.isBorder ?? true,
      isActive: detail.isActive ?? true,
      translation: detail.translation || detail.translations || [],
      pages: detail.pages?.length
        ? detail.pages.map((page) => ({
            navMenuId: page.navMenuId || "",
            sortOrder: page.sortOrder || 0,
            isActive: page.isActive ?? true,
          }))
        : [createEmptyPage()],
    });
  }, [detail]);

  useEffect(() => {
    if (detailError) toast.error(detailError.message || "Tải dữ liệu thất bại");
  }, [detailError, toast]);

  const getMenuTitle = (menu) => {
    const activeLangId = languages[activeTab]?.id;
    const translation =
      menu.translations?.find((t) => t.languageId === activeLangId) ||
      menu.translations?.[0];
    return (
      translation?.title ||
      translation?.name ||
      menu.title ||
      menu.name ||
      `Menu ${menu.id}`
    );
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleTranslationChange = (languageId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      translation: prev.translation.some(
        (item) => item.languageId === languageId,
      )
        ? prev.translation.map((item) =>
            item.languageId === languageId ? { ...item, [field]: value } : item,
          )
        : [
            ...prev.translation,
            {
              languageId,
              title: "",
              subtitle: "",
              description: "",
              [field]: value,
            },
          ],
    }));
  };

  const getNextSortOrder = async (navMenuId) => {
    if (!navMenuId) return 0;

    const response = await getAdminSectionContentCards({
      pageIndex: 1,
      pageSize: 1000,
      languageCode: "vi",
      navMenuId,
    });
    const cards = Array.isArray(response?.data)
      ? response.data
      : response?.data?.items || [];
    const maxSortOrder = cards.reduce((max, card) => {
      if (Number(card.id) === Number(id)) return max;

      const pageOrders = (card.pages || [])
        .filter((page) => Number(page.navMenuId) === Number(navMenuId))
        .map((page) => Number(page.sortOrder) || 0);
      const cardMax = pageOrders.length ? Math.max(...pageOrders) : 0;

      return Math.max(max, cardMax);
    }, 0);

    return maxSortOrder + 1;
  };

  const handlePageChange = async (index, field, value) => {
    if (field === "navMenuId") {
      setFormData((prev) => ({
        ...prev,
        pages: prev.pages.map((page, pageIndex) =>
          pageIndex === index
            ? {
                ...page,
                navMenuId: value,
                sortOrder: value ? page.sortOrder : 0,
              }
            : page,
        ),
      }));

      if (!value) return;

      try {
        const nextSortOrder = await getNextSortOrder(value);
        setFormData((prev) => ({
          ...prev,
          pages: prev.pages.map((page, pageIndex) =>
            pageIndex === index ? { ...page, sortOrder: nextSortOrder } : page,
          ),
        }));
      } catch {
        toast.error("Không lấy được thứ tự tự động");
      }
      return;
    }

    setFormData((prev) => ({
      ...prev,
      pages: prev.pages.map((page, pageIndex) =>
        pageIndex === index
          ? { ...page, [field]: field === "isActive" ? value : value }
          : page,
      ),
    }));
  };

  const handleAddPage = () => {
    setFormData((prev) => ({
      ...prev,
      pages: [...prev.pages, createEmptyPage()],
    }));
  };

  const handleRemovePage = (index) => {
    setFormData((prev) => ({
      ...prev,
      pages:
        prev.pages.length > 1
          ? prev.pages.filter((_, pageIndex) => pageIndex !== index)
          : prev.pages,
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setUploadingImage(true);
    try {
      const imageUrl = await uploadImage(file, { folder: "card" });
      setFormData((prev) => ({ ...prev, imgURL: imageUrl }));
      setErrors((prev) => ({ ...prev, imgURL: "" }));
      toast.success("Upload ảnh thành công");
    } catch (error) {
      toast.error(error?.message || "Upload ảnh thất bại");
    } finally {
      setUploadingImage(false);
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.imgURL.trim()) nextErrors.imgURL = "Vui lòng upload ảnh card";
    if (!formData.translation.some((item) => item.title?.trim()))
      nextErrors.translation = "Vui lòng nhập tiêu đề cho ít nhất một ngôn ngữ";
    if (!formData.translation.some((item) => item.description?.trim()))
      nextErrors.description = "Vui lòng nhập mô tả cho ít nhất một ngôn ngữ";
    if (!formData.pages.some((page) => page.navMenuId))
      nextErrors.pages = "Vui lòng chọn ít nhất một trang";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      imgURL: formData.imgURL.trim(),
      color: formData.color || "#ffffff",
      textColor: formData.textColor || "#123D2A",
      isBorder: Boolean(formData.isBorder),
      isActive: Boolean(formData.isActive),
      translation: formData.translation.map((item) => ({
        languageId: Number(item.languageId),
        title: item.title || "",
        subtitle: item.subtitle || "",
        description: item.description || "",
      })),
      pages: formData.pages
        .filter((page) => page.navMenuId)
        .map((page) => ({
          navMenuId: Number(page.navMenuId),
          sortOrder: Number(page.sortOrder) || 0,
          isActive: Boolean(page.isActive),
        })),
    };

    try {
      const res = id
        ? await updateMutation.mutateAsync({ id, data: payload })
        : await createMutation.mutateAsync(payload);
      if (res.success) {
        toast.success(id ? "Cập nhật thành công" : "Thêm mới thành công");
        navigate("/admin/section-content-cards");
      } else if (res.messageError) {
        setErrors(res.messageError);
      } else {
        toast.error(res.message || "Lưu thất bại");
      }
    } catch {
      toast.error("Lưu thất bại");
    }
  };

  return (
    <div className="admin-page">
      <PageHeader
        title={id ? "Cập nhật Card nội dung" : "Thêm Card nội dung"}
      />
      <form className="form-card" onSubmit={handleSubmit}>
        <p className="form-section-title">Cài đặt Card</p>
        <div className="input-group card-upload-field">
          <label htmlFor="cardImage">
            Ảnh Card<span className="required">*</span>
          </label>
          <input
            ref={imageInputRef}
            id="cardImage"
            name="cardImage"
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            disabled={uploadingImage}
            className="card-upload-input"
          />
          <button
            type="button"
            className={`card-upload-box ${formData.imgURL ? "has-image" : ""}`}
            onClick={() => imageInputRef.current?.click()}
            disabled={uploadingImage}
          >
            {formData.imgURL ? (
              <img
                src={formData.imgURL}
                alt=""
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <span>
                {uploadingImage ? "Đang upload ảnh..." : "Upload ảnh"}
              </span>
            )}
          </button>
          {uploadingImage && (
            <span className="field-help">Đang upload ảnh...</span>
          )}
          {formData.imgURL && (
            <span className="field-help">
              {getFileNameFromUrl(formData.imgURL)}
            </span>
          )}
          {errors.imgURL && (
            <span className="error-message">{errors.imgURL}</span>
          )}
        </div>
        <div className="form-row">
          <Input
            label="Màu viền"
            name="color"
            type="color"
            value={formData.color}
            onChange={handleChange}
          />
          <Input
            label="Màu chữ"
            name="textColor"
            type="color"
            value={formData.textColor}
            onChange={handleChange}
          />
        </div>
        <div className="form-row">
          <Checkbox
            label="Hiển thị viền"
            name="isBorder"
            checked={formData.isBorder}
            onChange={handleChange}
          />
          <Checkbox
            label="Hoạt động"
            name="isActive"
            checked={formData.isActive}
            onChange={handleChange}
          />
        </div>

        <p className="form-section-title">Trang sử dụng Card</p>
        {errors.pages && (
          <p
            className="error-message"
            style={{ color: "#dc2626", fontSize: 13 }}
          >
            {errors.pages}
          </p>
        )}
        {formData.pages.map((page, index) => (
          <div
            className="form-row"
            key={index}
            style={{ alignItems: "flex-end" }}
          >
            <TreeSelect
              label="Trang/Menu *"
              value={page.navMenuId}
              onChange={(e) =>
                handlePageChange(index, "navMenuId", e.target.value)
              }
              items={menus}
              placeholder="Chọn trang..."
              getLabel={getMenuTitle}
              required
            />
            <Input
              label="Thứ tự"
              type="number"
              value={page.sortOrder}
              onChange={(e) =>
                handlePageChange(index, "sortOrder", e.target.value)
              }
            />
            <Checkbox
              label="Hoạt động"
              checked={page.isActive}
              onChange={(e) =>
                handlePageChange(index, "isActive", e.target.checked)
              }
            />
            <Button
              type="button"
              variant="danger"
              onClick={() => handleRemovePage(index)}
              disabled={formData.pages.length === 1}
            >
              Xóa
            </Button>
          </div>
        ))}
        <div style={{ marginBottom: 20 }}>
          <Button type="button" variant="secondary" onClick={handleAddPage}>
            + Thêm Trang
          </Button>
        </div>

        {languages.length > 0 && (
          <>
            <p className="form-section-title">Bản dịch</p>
            {(errors.translation || errors.description) && (
              <p
                className="error-message"
                style={{ color: "#dc2626", fontSize: 13 }}
              >
                {errors.translation || errors.description}
              </p>
            )}
            <div className="translation-tabs">
              <div className="tab-header">
                {languages.map((lang, index) => (
                  <button
                    key={lang.id}
                    type="button"
                    className={`tab-btn ${activeTab === index ? "active" : ""}`}
                    onClick={() => setActiveTab(index)}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
              {languages.map((lang, index) => {
                const translation =
                  formData.translation.find(
                    (item) => item.languageId === lang.id,
                  ) || {};
                return activeTab === index ? (
                  <div key={lang.id} className="tab-content">
                    <Input
                      label="Tiêu đề *"
                      value={translation.title || ""}
                      onChange={(e) =>
                        handleTranslationChange(
                          lang.id,
                          "title",
                          e.target.value,
                        )
                      }
                      required
                    />
                    <Input
                      label="Phụ đề"
                      value={translation.subtitle || ""}
                      onChange={(e) =>
                        handleTranslationChange(
                          lang.id,
                          "subtitle",
                          e.target.value,
                        )
                      }
                    />
                    <div className="admin-form-group">
                      <label>Mô tả *</label>
                      <RichTextEditor
                        value={translation.description || ""}
                        onChange={(content) =>
                          handleTranslationChange(
                            lang.id,
                            "description",
                            content,
                          )
                        }
                        placeholder={`Nhập mô tả (${lang.name})...`}
                      />
                    </div>
                  </div>
                ) : null;
              })}
            </div>
          </>
        )}

        <div className="form-actions">
          <Button type="submit" disabled={loading}>
            {loading ? "Đang lưu..." : id ? "Cập nhật" : "Thêm mới"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate("/admin/section-content-cards")}
          >
            Hủy
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SectionContentCardsForm;
